import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useEffectiveRole } from "@/lib/effectiveRole";
import { useFollowUpCounts } from "@/hooks/useFollowUpCounts";
import { DESKTOP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import {
  House, Funnel, Briefcase, Buildings, CalendarBlank, ListChecks,
  FolderOpen, Image as ImageIcon, DotsThreeOutline, Coin, FileText, Pulse,
  Users, Gear, SignOut, X, Camera, Bed, ClockCounterClockwise, Lightning,
  ChatCircleDots, Receipt, Sun, BookOpenText,
} from "@phosphor-icons/react";

/**
 * Mobile-only bottom navigation. Compact, native-app-style tab bar.
 *
 * For role=investor (whose dashboard uses internal tab state, not routes)
 * items dispatch a window-level event picked up by InvestorDashboard, and
 * `activeTab` is used to compute the active state. Other roles use real
 * router routes and `useLocation()`. Admin and marketing also surface a
 * "Más" item that opens a bottom-sheet with secondary destinations.
 */

const MORE_KEY = "more";

const PRIMARY_BY_ROLE = {
  // Admin: Acción | Leads | Seguimientos | Conversaciones | Más — la misma
  // arquitectura que la barra lateral, en el orden del día de trabajo: qué
  // necesita atención, a quién se le vende, qué toca hacer y quién ha
  // escrito. Lo demás vive en "Más", agrupado igual que en escritorio.
  // `badge: "followups"` pinta vencidas + de hoy.
  admin: [
    { key: "actions",   to: "/actions",    label: "Acción",       Icon: Lightning },
    { key: "leads",     to: "/leads",      label: "Leads",        Icon: Funnel },
    { key: "followups", to: "/follow-ups", label: "Seguimientos", Icon: ClockCounterClockwise, badge: "followups" },
    { key: "conversations", to: "/conversaciones", label: "Conversaciones", Icon: ChatCircleDots },
    { key: MORE_KEY, label: "Más", Icon: DotsThreeOutline, more: true },
  ],
  marketing: [
    { key: "home",   to: "/",        label: "Inicio",  Icon: House },
    { key: "leads",  to: "/leads",   label: "Leads",   Icon: Funnel },
    { key: "alerts", to: "/alerts",  label: "Alertas", Icon: CalendarBlank },
    { key: MORE_KEY, label: "Más", Icon: DotsThreeOutline, more: true },
  ],
  constructor: [
    { key: "home",    to: "/",        label: "Inicio",  Icon: House },
    // "Avance" jumps into ConstructorDashboard's internal tab via
    // CustomEvent — no real route exists, mirrors the investor pattern.
    { key: "avance",  tab: "progress", event: "dv:constructor-tab", label: "Avance", Icon: Camera },
    { key: "alerts",  to: "/alerts",  label: "Alertas", Icon: CalendarBlank },
    { key: "tasks",   to: "/tasks",   label: "Tareas",  Icon: ListChecks },
  ],
  investor: [
    { key: "my_project",  tab: "my_project",  label: "Mi villa", Icon: House },
    { key: "progress",    tab: "progress",    label: "Updates",  Icon: ImageIcon },
    { key: "pms_preview", tab: "pms_preview", label: "Reservas", Icon: Bed },
  ],
};

// Lo que va dentro de "Más". Para admin, los mismos grupos que la barra
// lateral de escritorio: una sola arquitectura de información en los dos
// sitios, para que nadie tenga que aprenderse dos menús. Marketing solo ve
// Tareas (el resto le daría 404 / redirección).
const MORE_BY_ROLE = {
  admin: [
    { group: "PMS" },
    { key: "pmsToday",        to: "/pms/today",        label: "Hoy",        Icon: Sun },
    { key: "pmsReservations", to: "/pms/reservations", label: "Reservas",   Icon: Bed },
    { key: "pmsGuests",       to: "/pms/guests",       label: "Huéspedes",  Icon: Users },
    { key: "villas",          to: "/villas",           label: "Villas",     Icon: Buildings },
    { group: "Administración" },
    { key: "payments",  to: "/payments",  label: "Finanzas",        Icon: Coin },
    { key: "gastos",    to: "/admin/gastos", label: "Gastos internos", Icon: Receipt },
    { key: "documents", to: "/documents", label: "Data room",       Icon: FolderOpen },
    { key: "obra",      to: "/admin/operaciones", label: "Operaciones · inversores", Icon: Briefcase },
    { key: "users",     to: "/users",     label: "Usuarios",        Icon: Users },
    { key: "settings",  to: "/settings",  label: "Ajustes",         Icon: Gear },
    { group: "Dirección y sistema" },
    { key: "home",     to: "/dashboard", label: "Panel ejecutivo", Icon: House },
    { key: "tasks",    to: "/tasks",     label: "Tareas IA",       Icon: ListChecks },
    { key: "knowledge", to: "/admin/conocimiento", label: "Base de conocimiento", Icon: BookOpenText },
    { key: "activity", to: "/activity",  label: "Actividad",       Icon: Pulse },
  ],
  marketing: [
    { key: "tasks",    to: "/tasks",    label: "Tareas",    Icon: ListChecks },
    { key: "calendar", to: "/calendar", label: "Calendario", Icon: CalendarBlank, adminOnly: true },
  ],
};

const ACTIVE_COLOR = "#D8C9A3";   // warm cream — premium accent against dark bg
const INACTIVE_COLOR = "#8C8780"; // muted stone

function isRouteActive(pathname, to) {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

function MoreSheet({ open, onClose, items, onLogout }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Más opciones">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 bg-white dark:bg-stone-900 rounded-t-2xl shadow-[0_-8px_24px_rgba(0,0,0,0.18)] dark:shadow-[0_-8px_24px_rgba(0,0,0,0.6)]"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}
      >
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="text-[11px] tracking-[0.2em] uppercase text-stone-500 dark:text-stone-400">Más</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="w-8 h-8 flex items-center justify-center rounded-full text-stone-600 dark:text-stone-300 outline-none focus-visible:ring-2 focus-visible:ring-stone-300 dark:focus-visible:ring-stone-600"
          >
            <X size={18} />
          </button>
        </div>
        {/* Alto acotado + scroll propio: con los grupos la lista pasa de
            nueve a quince entradas y en un iPhone pequeño se salía de la
            pantalla, dejando "Ajustes" fuera de alcance. */}
        <ul className="px-2 pb-2 max-h-[70vh] overflow-y-auto overscroll-contain">
          {items.map(({ key, to, label, Icon, group }) => (
            group ? (
              <li
                key={`g-${group}`}
                className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-stone-400 dark:text-stone-500"
              >
                {group}
              </li>
            ) : (
            <li key={key}>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate(to);
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-stone-800 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 active:bg-stone-100 dark:active:bg-stone-700 outline-none focus-visible:bg-stone-50 dark:focus-visible:bg-stone-800"
              >
                <Icon size={20} />
                <span className="flex-1 text-left">{label}</span>
              </button>
            </li>
            )
          ))}
          <li className="border-t border-stone-100 dark:border-stone-800 mt-1 pt-1">
            <button
              type="button"
              onClick={() => {
                onClose();
                onLogout?.();
              }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-stone-800 dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 active:bg-stone-100 dark:active:bg-stone-700 outline-none focus-visible:bg-stone-50 dark:focus-visible:bg-stone-800"
            >
              <SignOut size={20} />
              <span className="flex-1 text-left">Cerrar sesión</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function MobileBottomNav({ activeTab }) {
  const { user, logout } = useAuth();
  const effectiveRole = useEffectiveRole();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const role = user ? (effectiveRole || user.role || "investor") : null;
  // Seguimientos badge (overdue + due_today) — same contract as the
  // AdminSidebar badge. Only fetched where this bar is actually visible
  // (below `lg`) and only for admin; refreshed on every route change so
  // a completed/snoozed follow-up updates the count on the next screen.
  // Silent on failure: no counts → no badge.
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const { counts: followUpCounts, reload: reloadFollowUpCounts } = useFollowUpCounts({
    enabled: role === "admin" && !desktop,
  });
  const followUpBadge = followUpCounts
    ? (followUpCounts.overdue || 0) + (followUpCounts.due_today || 0)
    : 0;

  // Close the sheet whenever the route changes (covers programmatic and
  // direct NavLink navigation) and refresh the follow-ups badge. The
  // first run is skipped: the hook already fetched on mount, so only a
  // REAL route change re-requests the counts (one request per page).
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    setMoreOpen(false);
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    if (role === "admin" && !desktop) reloadFollowUpCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!user) return null;
  // Use hasOwn / Array.isArray guards because role names like "constructor"
  // collide with Object.prototype.constructor — `MORE_BY_ROLE["constructor"]`
  // would otherwise return the Object constructor function, not undefined,
  // and `.filter` on it would throw at runtime (white-screen on login).
  const primaryRaw = Object.prototype.hasOwnProperty.call(PRIMARY_BY_ROLE, role)
    ? PRIMARY_BY_ROLE[role]
    : PRIMARY_BY_ROLE.investor;
  const items = Array.isArray(primaryRaw) ? primaryRaw : [];
  const moreRaw = Object.prototype.hasOwnProperty.call(MORE_BY_ROLE, role)
    ? MORE_BY_ROLE[role]
    : null;
  const moreItems = Array.isArray(moreRaw)
    ? moreRaw.filter((it) => !it.adminOnly || role === "admin")
    : [];

  return (
    <>
      <nav
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none"
        aria-label="Navegación principal"
        style={{
          // Safe-area as INTERNAL padding of the fixed container (not a
          // margin on the pill): margins re-layout during Safari's toolbar
          // collapse/expand and make the bar appear to float mid-screen.
          paddingBottom: "max(env(safe-area-inset-bottom, 0px), 6px)",
          // Own compositing layer — WebKit repaints fixed elements lazily
          // while the browser chrome animates; without this the bar lags
          // visibly behind the viewport edge on iOS.
          transform: "translateZ(0)",
        }}
      >
        {/* Dark dock/backdrop so the space below the pill (and the iOS home
            indicator area) feels anchored instead of showing a white gap. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            height: "calc(env(safe-area-inset-bottom, 0px) + 72px)",
            background:
              "linear-gradient(to bottom, rgba(28,28,30,0) 0%, rgba(28,28,30,0.85) 38%, #1C1C1E 70%)",
          }}
        />
        <div
          className="relative pointer-events-auto mx-3 rounded-2xl border border-white/5 shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
          style={{ background: "#1C1C1E" }}
        >
          <ul
            className="grid items-stretch"
            style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
          >
            {items.map((item) => {
              const { key, Icon, label } = item;
              const active = item.more
                ? moreOpen
                : item.tab
                ? activeTab === item.tab
                : isRouteActive(location.pathname, item.to);
              const color = active ? ACTIVE_COLOR : INACTIVE_COLOR;
              const indicator = (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-6 rounded-b-full"
                  style={{
                    background: active ? ACTIVE_COLOR : "transparent",
                    transition: "background 150ms ease",
                  }}
                  aria-hidden="true"
                />
              );
              const badgeCount = item.badge === "followups" ? followUpBadge : 0;
              const inner = (
                <span
                  className="relative w-full min-w-0 flex flex-col items-center justify-center gap-1 py-2.5 px-1"
                  style={{ color }}
                >
                  {indicator}
                  <span className="relative inline-flex">
                    <Icon size={22} weight={active ? "fill" : "regular"} />
                    {badgeCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] leading-4 text-center font-medium text-white"
                        style={{ background: "#9C5236" }}
                        aria-label={`${badgeCount} seguimientos pendientes`}
                        data-testid={`mobile-nav-badge-${item.key}`}
                      >
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] leading-tight tracking-wide truncate max-w-full">
                    {label}
                  </span>
                </span>
              );

              const sharedClass =
                "w-full h-full min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded-xl select-none";

              if (item.more) {
                return (
                  <li key={key} className="min-w-0">
                    <button
                      type="button"
                      onClick={() => setMoreOpen((v) => !v)}
                      className={sharedClass}
                      aria-haspopup="dialog"
                      aria-expanded={moreOpen}
                      aria-current={active ? "page" : undefined}
                    >
                      {inner}
                    </button>
                  </li>
                );
              }
              if (item.tab) {
                // Role-aware event name: investor (default) uses
                // "dv:investor-tab"; constructor passes item.event explicitly.
                const eventName = item.event || "dv:investor-tab";
                return (
                  <li key={key} className="min-w-0">
                    <button
                      type="button"
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent(eventName, { detail: item.tab })
                        )
                      }
                      className={sharedClass}
                      aria-current={active ? "page" : undefined}
                    >
                      {inner}
                    </button>
                  </li>
                );
              }
              return (
                <li key={key} className="min-w-0">
                  <NavLink
                    to={item.to}
                    className={sharedClass + " block"}
                    aria-current={active ? "page" : undefined}
                  >
                    {inner}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        items={moreItems}
        onLogout={logout}
      />
    </>
  );
}
